import { inject, Injectable } from "@angular/core";
import
{
    IGridLayout,
    IGridLayoutSelector,
    IMetadataAction,
    PropertyMetadata,
} from "@tylertech/tyler-api-types";
import { Utilities } from "@tylertech/shared-module";
import { CacheHelperService, SecurityService, SystemInfoService, SystemInfoExtended, NavigationService, CachedRepositoryService, FeatureFlagHelperService } from "@tylertech/core-module";
import { IGridState, IgxGridStateDirective } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { RepositoryFormData } from "../../interfaces";
import { MetadataActionService } from "../metadataActionService";
import { MetadataModel, MetadataPropertyService } from "../metadataPropertyService";
import { GridLayoutService } from "../gridLayoutService.g";
import { ProviderStorageService } from "./provider.storage.service";
import { ResetStateService } from "../reset.state.service";
import { gridLayoutSelectorType, gridLayoutType } from "../../constants";

@Injectable({ providedIn: "root" })
export class InfoGridLayoutService extends ResetStateService
{
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $securityService = inject(SecurityService);
    private $odataFilterFactory = inject(ODataFilterFactoryService);
    private $gridLayoutService = inject(GridLayoutService);
    private $repositoryService = inject(CachedRepositoryService);
    private readonly $cacheHelperService = inject(CacheHelperService);
    private readonly $providerStorageService = inject(ProviderStorageService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);

    DefaultLayout: Record<string, string> = {};

    GridLayoutId: Record<string, string> = {};

    LocalCachedLayout: Record<string, string> = {};

    EnableLayout: Record<string, boolean> = {};

    ScrollPosition: Record<string, string> = {};

    ActiveLayout: Record<string, IGridLayout> = {};

    SettingState: Record<string, boolean> = {};

    constructor()
    {
        super(inject(NavigationService));
    }

    SetState(grid: IgxGridComponent, state: IgxGridStateDirective, newLayout: string, gridColumns: any[]): void
    {
        this.SettingState[grid.id] = true;
        newLayout = this.applyLayoutChanges(newLayout, state);
        const editableColumnState: Record<string, boolean> = {};
        const summarizedColumnState: Record<string, boolean> = {};
        const alwaysHiddenColumnState: Record<string, boolean> = {};

        grid.columns.forEach(c =>
        {
            editableColumnState[c.field] = c.editable;
            summarizedColumnState[c.field] = c.hasSummary;
            alwaysHiddenColumnState[c.field] = gridColumns.find(x => x.Name === c.field)?.AlwaysHidden;
        });
        state.setState(newLayout);
        grid.columns.forEach(c =>
        {
            c.editable = editableColumnState[c.field];
            c.hasSummary = summarizedColumnState[c.field];
            c.hidden = c.hidden || alwaysHiddenColumnState[c.field];
        });
    }

    applyLayoutChanges(layout: string, state: IgxGridStateDirective): string
    {
        const layoutToApply: IGridState = JSON.parse(layout);
        const currentLayout: IGridState = JSON.parse(<string>state.getState());

        cleanArray(layoutToApply.columns, currentLayout, "field");
        cleanArray(layoutToApply.filtering.filteringOperands, currentLayout, "fieldName");
        cleanArray(layoutToApply.sorting, currentLayout, "fieldName");

        for (let i = 0; i < currentLayout.columns.length; i++)
        {
            const column = currentLayout.columns[i];
            const columnName = column.field;
            if (!layoutToApply.columns.some(x => x.field === columnName))
                layoutToApply.columns.push(column);
        }

        layoutToApply.columns.forEach(column => column.filteringIgnoreCase = false);

        function cleanArray(arrayToCheck: any[], currentLayout: IGridState, fieldProperty: string): void
        {
            for (let i = arrayToCheck.length - 1; i >= 0; i--)
            {
                const column = arrayToCheck[i];
                const columnName = column[fieldProperty];
                if (!currentLayout.columns.some(x => x.field === columnName))
                    arrayToCheck.splice(arrayToCheck.indexOf(column), 1);
            }
        }

        return JSON.stringify(layoutToApply);
     }

    LayoutEnabled(providerId: string): Promise<boolean>
    {
        const self = this;
        return this.$systemInfoService.GetCachedSystemInfo()
            .then((systemInfo: SystemInfoExtended) =>
            {
                self.EnableLayout[providerId] = !systemInfo.IsTest() && !!self.GridLayoutId[providerId];
                return self.EnableLayout[providerId];
            });
    }

    SaveLayout(providerId: string, state: IgxGridStateDirective, local: boolean): void
    {
        const gridState = state.getState(true);
        const layout = typeof gridState === "string" ? gridState : JSON.stringify(gridState);
        if (local)
        {
            this.LocalCachedLayout[providerId] = layout;
            return;
        }
        delete this.DefaultLayout[providerId];
        const gridLayout = <MetadataModel>{
            UserId: this.$securityService.principle.UserId,
            GridId: this.GridLayoutId[providerId],
            ActiveLayout: this.getActiveLayoutDisplay(providerId),
            CreateNewLayout: true,
            Layout: layout,
            IsDefault: false,
            IsPublic: false,
            SerializedRowState: "Added"
        };
        const PropertyMetadata = <PropertyMetadata>{
            Name: "SaveLayout",
            RecalculateOnChange: ["UserId", "GridId", "Layout", "UpdateActiveVisible"],
            RecalculateWithParent: false,
        };
        this.$metadataPropertyService.recalculateProperties(
            gridLayoutType,
            PropertyMetadata,
            gridLayout,
            null,
            true,
            undefined,
            false,
            false,
            new RepositoryFormData(gridLayoutType, gridLayout)
        );
    }

    HasLocalCachedLayout(providerId: string): boolean
    {
        return !Utilities.IsNullOrUndefined(this.LocalCachedLayout[providerId]);
    }

    LoadLayout(providerId: string, local: boolean): Promise<string>
    {
        const self = this;
        if (local)
            return Promise.resolve(this.LocalCachedLayout[providerId]);

        const gridLayoutSelector = <IGridLayoutSelector>{
            UserId: this.$securityService.principle.UserId,
            GridId: this.GridLayoutId[providerId],
            ActiveLayout: this.getActiveLayoutDisplay(providerId)
        };
        return this.$gridLayoutService.LoadLayoutAction(gridLayoutSelector)
            .then((action: IMetadataAction) =>
            {
                return self.$metadataActionService
                    .ExecuteAction(
                        gridLayoutSelector,
                        action,
                        new RepositoryFormData(gridLayoutSelectorType, gridLayoutSelector)
                    )
                    .then((model: any) =>
                    {
                        if (!!model && !("canceledActionDialog" in model))
                        {
                            self.ActiveLayout[providerId] = model.Layout;
                            return model.Layout.Layout;
                        }
                    });
            });
    }

    FetchDefaultLayout(providerId: string): Promise<string>
    {
        const self = this;
        if (!Utilities.IsNullOrUndefined(self.DefaultLayout[providerId]))
            return Promise.resolve(self.DefaultLayout[providerId]);
        const odata = self.$odataFilterFactory
            .createBuilder()
            .filterEQ("IsDefault", true)
            .filterEQ("GridId", self.GridLayoutId[providerId])
            .constantFilter(`UserId eq '${Utilities.escapeODataString(self.$securityService.principle.UserId)}'`);
        return self.$repositoryService.Fetch(gridLayoutType, odata.build())
            .then((result: any[]) =>
            {
                if (result.length > 0)
                {
                    self.ActiveLayout[providerId] = result[0];
                    self.DefaultLayout[providerId] = result[0].Layout;
                    return result[0].Layout;
                }
                self.DefaultLayout[providerId] = "";
                return null;
            });
    }

    DeleteLayout(providerId: string): void
    {
        const self = this;
        const gridLayoutSelector = <IGridLayoutSelector>{
            UserId: this.$securityService.principle.UserId,
            GridId: this.GridLayoutId[providerId],
            OwnLayout: true,
        };
        this.$gridLayoutService.LoadLayoutAction(gridLayoutSelector)
            .then((action: IMetadataAction) =>
            {
                action.Label = "Delete Layout";
                self.$metadataActionService
                    .ExecuteAction(
                        gridLayoutSelector,
                        action,
                        new RepositoryFormData(gridLayoutSelectorType, gridLayoutSelector)
                    )
                    .then((model: any) =>
                    {
                        if (!!model && !("canceledActionDialog" in model))
                        {
                            model.Layout.SerializedRowState = "Deleted";
                            self.$repositoryService
                                .Save({
                                    Item1: gridLayoutType,
                                    Item2: Utilities.stringify(model.Layout),
                                })
                                .then(_x =>
                                {
                                    const propertyMetadata = action.ViewToShow.ViewMetaData.Properties.find(
                                        x => x.Name === "SelectedLayout"
                                    );
                                    self.$cacheHelperService.clearAllForType(propertyMetadata.Lookup);
                                });
                        }
                    });
            });
    }

    getActiveLayoutDisplay(providerId: string): string
    {
        if (this.ActiveLayout[providerId])
        {
            const active = this.ActiveLayout[providerId];
            let isDefault = "";
            let isPublic = "";
            if (active.IsDefault)
                isDefault = " (Default)";
            if (active.IsPublic)
                isPublic = " (Public)";
            return `Active Layout: ${active.Name}${isDefault}${isPublic}`;
        }
        return "No Active Layout";
    }

    SetScrollPosition(providerId: string, row: any, grid: IgxGridComponent): void
    {
        let scrollPosition = this.$providerStorageService.GridData[providerId].indexOf(row);
        scrollPosition = scrollPosition === -1 ? 0 : scrollPosition;
        this.ScrollPosition[providerId] = scrollPosition.toString();
    }

}
